import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RadioGroup } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckIcon, 
  SparklesIcon, 
  BoltIcon, 
  TicketIcon, 
  CreditCardIcon,
  CircleStackIcon,
  PlusCircleIcon,
  AcademicCapIcon,
  TagIcon
} from '@heroicons/react/24/solid';
import { SubscriptionPlanResponse } from '../Types/SubscriptionPlanType';
import { TaxQuoteResponse } from '../../Admin/Types/TaxConfigType';
import {
  CheckoutGateway,
  GatewayName,
  PaymentResponse,
} from '../Types/PaymentType';
import {
  activateTrialPlan,
  cancelledPayment,
  createPayment,
  getCheckoutGateways,
  quoteTax,
  verifyPayment,
} from '../Services/PaymentService';
import { openCheckout } from '../Utils/gatewayCheckout';
import PaymentCheckoutModal from './PaymentCheckoutModal';
import PaymentSuccessModal from './PaymentSuccessModal';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { storage } from '../../Shared/Utils/authStorage';
import { getCompanyById } from '../../Company/Services/CompanyService';
import { ReceiptCompany } from '../../Shared/Utils/generatePaymentReceiptPDF';

interface Props {
  plans: SubscriptionPlanResponse[];
  onSelect: (plan: SubscriptionPlanResponse) => void;
}

const SubscriptionUI: React.FC<Props> = ({ plans, onSelect }) => {
  /**
   * `subscription_plans.price` is nullable, and a plan saved with the admin form's Price box left
   * empty arrives here as null. Every amount below is formatted or multiplied without a guard, so
   * one such row used to blank the whole pricing page.
   *
   * <p>Coerced to a number here, once, at the boundary - and the fact that it was missing is kept
   * on the plan so the card can say so rather than claim the plan is free. A plan with no price is
   * a broken row, not a free one, and it should be corrected by an admin.
   */
  const visiblePlans = useMemo(() => {
    return plans
      .filter((p) => p.isVisibleOnUI && p.active)
      .map((p) => {
        const numeric = Number(p.price);
        const priceIsUnset = p.price === null || p.price === undefined || !Number.isFinite(numeric);
        return { ...p, price: priceIsUnset ? 0 : numeric, priceIsUnset };
      })
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [plans]);

  /**
   * The three tabs, derived from the plan name.
   *
   * <p>Matched by substring because that is all the data supports. There is no `plan_type` column on
   * `subscription_plans` - `planCode` and `planType` are declared on the frontend response type but
   * the API never populates them, so they are always undefined. A name that says "Data Service" is
   * the only signal available.
 *
   * <p>Data-service is tested before add-on so a plan called something like "Data Add-on" lands in the
   * tab that describes it rather than the one that describes how it is billed.
   *
   * <p>The patterns are whitespace-tolerant on purpose. The backend decides whether a purchase
   * overwrites a company's subscription by testing the plan name for the literal substring
 * *with a space*, `"add on"` - so "Add-On" and "ADDON" are visible here but are not recognised there.
   * See the note on the tab change below.
   */
  const categorizedPlans = useMemo(() => {
    const subscription: SubscriptionPlanResponse[] = [];
    const addOn: SubscriptionPlanResponse[] = [];
    const dataService: SubscriptionPlanResponse[] = [];

    visiblePlans.forEach((plan) => {
      const haystack = `${plan.planCode || ''} ${plan.name || ''}`.toUpperCase();
      const isDataService =
        Boolean((plan as any).isDataService) || Boolean((plan as SubscriptionPlanResponse & { isDataService?: boolean }).isDataService) ||
        /(DATA\s*SERVICE|DATASERVICE)/.test(haystack);

      if (isDataService) {
        dataService.push(plan);
      } else if (/(ADD\s*ON|ADDON|TOP\s*UP|TOPUP|EXTRA)/.test(haystack) || Boolean((plan as any).isAddon)) {
        addOn.push(plan);
      } else {
        // Everything else, free trials included, is a plain subscription.
        subscription.push(plan);
      }
    });

    return { subscription, addOn, dataService };
  }, [visiblePlans]);

  const [selected, setSelected] = useState<SubscriptionPlanResponse | undefined>();
  const [alert, setAlert] = useState<AlertProps | null>(null);

  /**
   * The selectable gateways, straight from the server.
   *
   * <p>Nothing here decides which providers exist. The backend answers with only the gateways an
   * admin has enabled with complete credentials, so configuring Cashfree in the admin area is the
   * entire process for it to appear here - no deploy, no rebuild, no environment variable. The
   * previous implementation hardcoded Razorpay and read its key from
   * `process.env.REACT_APP_RAZORPAY_KEY_ID`, which meant the key could only be changed by
   * rebuilding the frontend.
   */
  const [gateways, setGateways] = useState<CheckoutGateway[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<CheckoutGateway | null>(null);
  const [gatewaysLoaded, setGatewaysLoaded] = useState(false);

  const [customerPhone, setCustomerPhone] = useState('');
  const [processing, setProcessing] = useState(false);

  /**
   * Whether the payment step is open.
   *
   * <p>The payment method is asked for here rather than on the plan page. Choosing a provider is a
   * decision made once the plan and its price are already settled; surfacing it earlier made the
   * customer commit to the least important part of the purchase while still comparing plans.
   */
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  type PlanTab = 'subscription' | 'addOn' | 'dataService';
  const [activeTab, setActiveTab] = useState<PlanTab>('subscription');

  /**
   * Only the tabs that have plans in them.
   *
   * <p>An empty tab is worse than no tab: it advertises a category the account cannot buy from and
   * sends the customer to a blank screen. Data Service in particular will be absent until plans are
   * named to match it.
   */
  const tabs = useMemo(() => {
    const candidates: { id: PlanTab; label: string; blurb: string; icon: React.ElementType; plans: SubscriptionPlanResponse[] }[] = [
      {
        id: 'subscription',
        label: 'Subscription Plans',
        blurb: 'Full platform access for continuous recruitment',
        icon: AcademicCapIcon,
        plans: categorizedPlans.subscription,
      },
      {
        id: 'addOn',
        label: 'Add-ons & Top-ups',
        blurb: 'Boost your workspace with extra posting credits',
        icon: PlusCircleIcon,
        plans: categorizedPlans.addOn,
      },
      {
        id: 'dataService',
        label: 'Data Service',
        blurb: 'Candidate and company data, delivered to your workspace',
        icon: CircleStackIcon,
        plans: categorizedPlans.dataService,
      },
    ];

    return candidates.filter((tab) => tab.plans.length > 0);
  }, [categorizedPlans]);

  const visibleTabPlans = useMemo(() => {
    return tabs.find((tab) => tab.id === activeTab)?.plans ?? [];
  }, [tabs, activeTab]);

  /**
   * Switching tabs moves the selection with it.
   *
   * <p>Leaving a plan selected that is not on screen would mean the summary card and the Pay button
   * were both charging for something the customer can no longer see, one tab away. So the first plan
   * in the tab they just opened becomes the selection.
   */
  const handleTabChange = (tab: PlanTab) => {
    setActiveTab(tab);
    const plans = tabs.find((candidate) => candidate.id === tab)?.plans ?? [];
    if (plans.length > 0 && !plans.some((plan) => plan.id === selected?.id)) {
      setSelected(plans[0]);
    }
  };

  /**
   * The tax-inclusive total for the selected plan.
   *
   * <p>Fetched from the server rather than computed here, because tax is added on top of the plan
   * price: showing a locally computed figure would mean the number on screen and the number charged
   * are produced by two implementations of the same rounding rule. This is what the customer is
   * shown; the charge itself is always recomputed server-side from the plan price.
   */
  const [quote, setQuote] = useState<TaxQuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  /**
   * Whether tax is switched on, latched once the first quote lands.
   *
   * <p>Monotonic on purpose. The plan cards are rendered before any quote exists, and a hint that
   * appeared and disappeared as the customer moved between plans would read as a pricing glitch.
   * A page load is the right granularity for "is tax on" - the bar itself always shows the
   * authoritative figure for the plan actually selected.
   */
  const [taxActive, setTaxActive] = useState(false);

  useEffect(() => {
    const price = selected?.price ?? 0;

    // A free plan never reaches a gateway, so there is nothing to quote.
    if (!selected || price <= 0) {
      setQuote(null);
      return;
    }

    let cancelled = false;
    setQuoteLoading(true);

    quoteTax(price)
      .then((response) => {
        if (!cancelled && response?.success && response.data) {
          setQuote(response.data);
          setTaxActive((prev) => prev || Boolean(response.data!.taxEnabled));
        }
      })
      .catch(() => {
        // The bar falls back to the bare plan price. The charge is unaffected - the server always
        // recomputes - so a failed quote must not block checkout.
        if (!cancelled) setQuote(null);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selected?.id, selected?.price]);

  const [completedPayment, setCompletedPayment] = useState<PaymentResponse | null>(null);
  const [completedGateway, setCompletedGateway] = useState<CheckoutGateway | null>(null);
  const [company, setCompany] = useState<ReceiptCompany | null>(null);

  useEffect(() => {
    if (!selected && visiblePlans.length > 0) {
      const defaultPlan = visiblePlans.find((p) => p.isDefault) || visiblePlans[0];
      setSelected(defaultPlan);
    }
  }, [visiblePlans, selected]);

  useEffect(() => {
    const load = async () => {
      const response = await getCheckoutGateways();
      const available = response?.success && Array.isArray(response.data) ? response.data : [];

      setGateways(available);
      // Defaults to the first, which is the one the admin put at the top of the list.
      setSelectedGateway(available.length > 0 ? available[0] : null);
      setGatewaysLoaded(true);
    };

    load();
  }, []);

  const handleSelection = (plan: SubscriptionPlanResponse) => {
    setSelected(plan);
    onSelect(plan);
  };

  const showError = (title: string, message: string) => {
    // Alerts render at the top of the page, behind the checkout dialog. Closing it here is what makes
    // a failure visible at all - otherwise the customer sits in a dialog whose confirm button has
    // stopped responding, with the reason displayed behind it.
    setCheckoutOpen(false);
    setAlert({ variant: 'error', title, message, autoCloseMs: 6000, onClose: () => setAlert(null) });
  };

  /**
   * One wording for "there is nothing to pay with", whether it is discovered before the dialog opens
   * or at the point the order is created. Two copies of this string would eventually disagree, and the
   * customer would get a different answer depending on which button they happened to press.
   */
  const reportNoGateway = () =>
    showError(
      'No payment method available',
      gatewaysLoaded
        ? 'No payment method is currently enabled. Please contact your company admin.'
        : 'Loading payment methods, please try again in a moment.'
    );

  /**
   * The page's single call to action.
   *
   * <p>A free plan goes straight to activation - it never reaches a gateway, so opening a payment
   * dialog for one would ask for a payment method that will not be used. Everything else opens the
   * payment step.
   */
  const startCheckout = () => {
    if (!isPaidPlan) {
      handleSubmit();
      return;
    }

    if (!selectedGateway) {
      reportNoGateway();
      return;
    }

    setCheckoutOpen(true);
  };

  const handleDismiss = async (paymentId?: string) => {
    if (paymentId) {
      await cancelledPayment(paymentId);
    }
    setProcessing(false);
    setAlert({
      variant: 'error',
      title: 'Cancelled',
      message: 'Payment process was cancelled. Nothing was charged.',
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });
  };

  /** Reads the company record the receipt header needs. Never blocks the success screen. */
  const loadCompany = useCallback(async (companyId?: string) => {
    if (!companyId) {
      return;
    }
    try {
      const response = await getCompanyById(companyId);
      if (response?.success && response.data) {
        setCompany(response.data);
      }
    } catch {
      // The receipt falls back to the platform name, which is acceptable.
    }
  }, []);

  /**
   * The one place a verified payment becomes a successful payment.
   *
   * <p>Guarded by the status check rather than by `response.success`: a 200 with a payment still in
   * PENDING means the gateway has not confirmed it, and treating that as success would hand out
   * credits for an unpaid order.
   */
  const completePayment = async (payment: PaymentResponse, gateway: CheckoutGateway | null) => {
    setCheckoutOpen(false);
    setCompletedPayment(payment);
    setCompletedGateway(gateway);
    setProcessing(false);
    await loadCompany(payment.companyId);
  };

  /**
   * Hands the provider's callback to the backend for verification.
   *
   * <p>The provider is sent by name and the callback by field, but none of it is evidence - the
   * backend's gateway implementation recomputes the provider's signature or queries its status API
   * before anything is credited. This function's only job is to forward and to render the outcome.
   */
  const handleProviderCallback = async (
    gateway: CheckoutGateway,
    paymentId: string,
    params: Record<string, string>
  ) => {
    setProcessing(true);

    const orderId =
      params.razorpay_order_id || params.orderId || params.order_id || params.txnid || '';

    const response = await verifyPayment({
      gateway: gateway.gateway as GatewayName,
      gatewayOrderId: orderId,
      gatewayPaymentId:
        params.razorpay_payment_id || params.paymentId || params.payuId || params.cf_payment_id || undefined,
      gatewaySignature:
        params.razorpay_signature || params.hash || params.CHECKSUMHASH || params.cf_signature || undefined,
      status: params.status || params.resultCode || undefined,
      params,
    });

    if (response?.success && response.data?.paymentStatus === 'SUCCESS') {
      await completePayment(response.data, gateway);
      return;
    }

    setProcessing(false);
    showError(
      'Payment not verified',
      response?.message || 'We could not verify this payment. If money was deducted it is released automatically.'
    );
  };

  const handleSubmit = async () => {
    if (!selected) {
      return;
    }

    // A free plan never reaches a gateway, so it needs no payment method at all.
    if (selected.price === 0) {
      const response = await activateTrialPlan({
        recruiterId: storage.getUserId(),
        recruiterName: 'Nexgent',
        paymentPlan: selected.name.toUpperCase(),
        amount: 0,
        creditsPurchased: selected.credits || 0,
      } as any);

      if (response?.success) {
        setAlert({
          variant: 'success',
          title: 'Success',
          message: 'Trial Plan Activated Successfully!',
          autoCloseMs: 5000,
        });
        window.setTimeout(() => window.location.reload(), 3000);
        return;
      }

      showError('Error', response?.message || 'Unable to activate the trial plan');
      return;
    }

    // The dialog disables its confirm button when there is nothing to choose from, so reaching here
    // without a gateway means the list changed underneath us. Worth one cheap check: creating an
    // order with no provider would fail deeper in, with a far worse message.
    if (!selectedGateway) {
      reportNoGateway();
      return;
    }

    setProcessing(true);
    setAlert(null);

    try {
      const payload = {
        recruiterId: storage.getUserId(),
        recruiterName: 'Nexgent',
        paymentPlan: selected.name.toUpperCase(),
        amount: selected.price,
        creditsPurchased: selected.credits || 0,
        gateway: selectedGateway.gateway,
        // Only forwarded when supplied; Cashfree is the provider that requires it.
        ...(customerPhone.trim() ? { customerPhone: customerPhone.trim() } : {}),
      };

      const response = await createPayment(payload as any);
      const payment = response?.data;

      if (!response?.success || !payment) {
        setProcessing(false);
        showError('Error', response?.message || 'Unable to initiate payment');
        return;
      }

      const checkout = payment.checkout || {};

      await openCheckout(
        selectedGateway.gateway,
        selectedGateway.scriptUrl,
        {
          ...checkout,
          description: `${selected.name} Plan Purchase`,
          amount: checkout.amount ?? Math.round(selected.price * 100),
          currency: checkout.currency ?? 'INR',
        },
        // Razorpay and Paytm complete without leaving the page; Cashfree and PayU redirect to
        // /payment/return, which verifies on arrival. This callback only fires for the former.
        (params) => handleProviderCallback(selectedGateway, payment.id, params),
        () => handleDismiss(payment.id),
        () => setProcessing(false)
      );
    } catch (error: any) {
      setProcessing(false);
      showError('Error', error?.message || 'Unable to initiate payment');
    }
  };

  if (visiblePlans.length === 0 || !selected) {
    return null;
  }

  // Helper Card Renderer to reduce duplicated DOM code
  const renderPlanCard = (plan: SubscriptionPlanResponse) => {
    const isChecked = selected?.id === plan.id;
    return (
      <RadioGroup.Option
        key={plan.id}
        value={plan}
        className={`
          relative flex flex-col p-1 transition-all duration-300 cursor-pointer rounded-[2rem] sm:rounded-[2.5rem]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950
          ${isChecked 
            ? 'bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 shadow-[0_0_50px_-12px_rgba(99,102,241,0.5)] z-10' 
            : 'bg-slate-800/40 hover:bg-slate-800/80 border border-slate-800'}
        `}
      >
        <div className="bg-slate-900 rounded-[1.9rem] sm:rounded-[2.4rem] p-5 sm:p-7 h-full flex flex-col justify-between">
          {(plan.priority && plan.priority > 1) && (
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="flex items-center gap-1.5 px-3 sm:px-4 py-1 rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-300 to-yellow-500 text-slate-950 shadow-xl border border-white/20 whitespace-nowrap">
                <SparklesIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-950" /> POPULAR CHOICE
              </span>
            </div>
          )}

          <div>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="text-indigo-400 font-mono text-[10px] sm:text-xs font-bold tracking-wider uppercase mb-1 truncate">
                  {plan.planCode || 'SUBSCRIPTION'}
                </p>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{plan.name}</h3>
              </div>
              <div className={`p-2 sm:p-2.5 rounded-2xl flex-shrink-0 ${isChecked ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-500'}`}>
                <BoltIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>
            
            <div className="mt-5 sm:mt-6 flex items-baseline gap-1.5">
              <span className="text-4xl sm:text-5xl font-black tracking-tight text-white">
                {(plan as SubscriptionPlanResponse & { priceIsUnset?: boolean }).priceIsUnset
                  ? 'Price not set'
                  : plan.price === 0
                    ? 'Free'
                    : `₹${plan.price.toLocaleString('en-IN')}`}
              </span>
              {plan.price > 0 && (
                <span className="text-slate-400 font-medium text-xs sm:text-sm lowercase">/{plan.billingCycle || 'mo'}</span>
              )}
            </div>

            {(plan as SubscriptionPlanResponse & { priceIsUnset?: boolean }).priceIsUnset && (
              <p className="mt-1.5 text-[11px] text-amber-400/90">
                This plan has no price saved, so it cannot be charged for. An admin needs to set one.
              </p>
            )}

            {/* Tax is added on top of the plan price, so the card says so rather than letting the
                checkout bar's higher total look like a discrepancy. */}
            {plan.price > 0 && taxActive && (
              <p className="mt-1.5 text-[11px] text-slate-500">+ GST at checkout</p>
            )}

            {plan.credits !== undefined && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-[11px] sm:text-xs font-bold">
                <TicketIcon className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                {plan.credits} Job Credits Included
              </div>
            )}

            {plan.discountPercent ? (
              <div className="mt-3 text-emerald-400 text-[11px] sm:text-xs font-bold flex items-center gap-1.5">
                <TagIcon className="w-4 h-4 flex-shrink-0" /> {plan.discountPercent}% OFF with {plan.couponCode}
              </div>
            ) : null}

            <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-slate-800/80 space-y-2.5 sm:space-y-3">
              {plan.credits && (
                <div className="flex items-center text-[11px] sm:text-xs text-slate-200 font-semibold">
                  <CheckIcon className="w-4 h-4 mr-2.5 text-indigo-400 flex-shrink-0" />
                  {plan.credits} Premium Postings
                </div>
              )}
              {plan.features && plan.features.map((feat, i) => (
                <div key={i} className="flex items-start text-[11px] sm:text-xs text-slate-400 font-medium">
                  <CheckIcon className="w-4 h-4 mr-2.5 mt-px text-indigo-500 flex-shrink-0" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`mt-6 sm:mt-8 w-full py-3 px-5 rounded-2xl font-black text-[11px] sm:text-xs uppercase text-center tracking-widest transition-all duration-300 ${
              isChecked
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                : 'bg-slate-800/90 text-slate-300'
            }`}
          >
            {isChecked ? 'Selected' : 'Select'}
          </div>
        </div>
      </RadioGroup.Option>
    );
  };

  const isPaidPlan = (selected.price ?? 0) > 0;
  const needsPhone = selectedGateway?.gateway === 'CASHFREE';

  /**
   * Whether the quote carries tax lines worth itemising.
   *
   * <p>Keyed on the amount rather than on {@code taxEnabled}, because a switched-on configuration
   * with rates still at zero produces a quote whose tax is legitimately nil - and printing a
   * "GST 0%" line for that is noise on the one screen the customer is deciding on.
   */
  const hasQuoteTax = Boolean(quote && quote.totalTaxAmount > 0);
  const displayedTotal = hasQuoteTax ? quote!.grandTotal : selected.price;
  const inr = (value: number | null | undefined) =>
    `₹${(Number.isFinite(Number(value)) ? Number(value) : 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  /**
   * The tax lines, handed to the payment dialog. Nothing on this page shows them - see
   * {@link selectionSummary}.
   *
   * <p>Only genuinely non-zero components are included. A switched-on configuration with rates still
   * at zero produces a quote whose tax is legitimately nil, and printing a "GST 0%" line for that is
   * noise on the screen where the customer is actually asked to pay.
   */
  const taxLines = hasQuoteTax
    ? ([
        { label: `CGST (${quote!.cgstPercent}%)`, amount: quote!.cgstAmount },
        { label: `SGST (${quote!.sgstPercent}%)`, amount: quote!.sgstAmount },
        { label: `IGST (${quote!.igstPercent}%)`, amount: quote!.igstAmount },
      ].filter((line) => line.amount > 0) as { label: string; amount: number }[])
    : [];

  /**
   * The plan, named once, in the floating bar.
   *
   * <p>Deliberately name-and-credits only - no tax lines, no total, no gateway. The bar answers
   * which plan is selected; the amount belongs to the charge, which the payment dialog settles and
   * itemises. Printing a total here put two different numbers for the same plan on one screen.
   */
  const selectionSummary = (
    <>
      <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest sm:text-xs">Selected plan</p>
      <p className="text-sm font-bold break-words sm:text-base">
        {selected.name}
        {selected.credits ? ` • ${selected.credits} Credits` : ''}
      </p>
    </>
  );

  /**
   * The one action on the page, and deliberately unlabelled with an amount.
   *
   * <p>It does not say what the charge is, because it does not make the charge - it opens the step
   * that does. Printing a total here would put a number on screen that the dialog then itemises
   * differently, and the page would be quoting something it does not itself settle.
   */
  const renderPayButton = () => (
    <button
      type="button"
      onClick={startCheckout}
      disabled={processing}
      className="w-full sm:w-auto flex-shrink-0 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3 sm:px-8 rounded-2xl font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-indigo-500/25"
    >
      <CreditCardIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
      <span className="whitespace-nowrap">
        {processing ? 'Processing…' : isPaidPlan ? 'Pay Now' : 'Activate Free Plan'}
      </span>
    </button>
  );

return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient wash behind the header. Decorative only, so it is hidden from assistive tech and
          cannot intercept the taps meant for the cards underneath. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(55%_45%_at_50%_0%,rgba(99,102,241,0.20),transparent_70%)]"
      />

      {/* pb-56 on phones clears the selection bar, which stacks to three rows there and is therefore
          taller than the single row it becomes from sm up (where pb-44 is enough). */}
      <div className="relative mx-auto w-full max-w-7xl px-4 pt-28 pb-56 sm:px-6 sm:pb-44 lg:px-8">
        {alert && (
          <div className="mx-auto mb-8 max-w-3xl">
            <Alert {...alert} onClose={() => setAlert(null)} />
          </div>
        )}

        <header className="mb-10 text-center sm:mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-indigo-400 font-bold tracking-widest uppercase text-[10px] sm:text-xs lg:text-sm"
          >
            Flexible Infrastructure
          </motion.h2>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-3 text-3xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-indigo-400"
          >
            Choose your power level.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-4 max-w-xl text-sm text-slate-400 sm:text-base"
          >
            Every paid plan is a one-off charge for job credits. Taxes are shown in full
            before you pay.
          </motion.p>
        </header>

        {/* Full width: the plans own the page. The checkout used to sit in a sticky rail on the right,
            which put the price and the Pay Now button on screen beside every card and split the
            decision between two columns. It is now the floating bar at the bottom, so the grid
            below gets the full width and the page reads top to bottom in one column. */}
        <div className="space-y-7 sm:space-y-8">
          {/* The three categories, as tabs rather than stacked sections. Previously all three
              scrolled as one long page, so reaching Add-ons meant scrolling past every
              subscription card - the pricing research is consistent that customers scan for the
              tier they came for and bounce when they have to hunt. Tabs also make the three
              kinds of purchase read as genuinely different things, which matters here because a
              Data Service purchase and a subscription are not alternatives to each other. */}
          <div
            role="tablist"
            aria-label="Plan categories"
            className="grid grid-cols-1 gap-2 rounded-3xl border border-white/10 bg-slate-900/60 p-1.5 sm:grid-cols-3 sm:gap-0"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;

              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors sm:justify-center sm:px-4 ${
                    isActive
                      ? 'bg-indigo-500/15 ring-1 ring-inset ring-indigo-500/40'
                      : 'hover:bg-white/5'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl transition-colors ${
                      isActive ? 'bg-indigo-500/20 text-indigo-300' : 'bg-slate-800/80 text-slate-500'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span
                      className={`block text-[13px] font-bold leading-tight sm:text-sm ${
                        isActive ? 'text-white' : 'text-slate-300'
                      }`}
                    >
                      {tab.label}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                      {tab.plans.length} {tab.plans.length === 1 ? 'plan' : 'plans'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <RadioGroup value={selected} onChange={handleSelection} className="block">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
            >
              {visibleTabPlans.map(renderPlanCard)}
            </motion.div>
          </RadioGroup>
        </div>
      </div>

      {/* The selection bar. One element at every width, floating over the bottom of the page: the
          plan on the left, the price and the single action on the right. There is nothing to
          expand - with tax moved to the dialog it is just the plan, the price and the button.

          Centred with auto margins, not `-translate-x-1/2`. framer-motion writes its own inline
          `transform` for the y animation, which replaces the Tailwind transform entirely - so
          `left-1/2 -translate-x-1/2` lost its -50% shift and hung half off the right edge on a
          phone. */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="selection-bar"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            className="fixed bottom-4 left-0 right-0 z-40 mx-auto w-[92%] max-w-xl sm:bottom-6"
          >
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-white/10 bg-slate-900/90 px-4 py-4 text-center shadow-2xl shadow-black/50 backdrop-blur-xl sm:flex-row sm:justify-center sm:gap-8 sm:px-8">
              <div className="w-full min-w-0 sm:w-auto">{selectionSummary}</div>
              <div className="flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row sm:gap-8">
                <div className="sm:text-right">
                  <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest sm:text-xs">Plan price</p>
                  <p className="text-lg font-black text-indigo-400 sm:text-xl">
                    {(selected as SubscriptionPlanResponse & { priceIsUnset?: boolean }).priceIsUnset
                      ? 'Not set'
                      : selected.price === 0
                        ? 'Free'
                        : inr(selected.price)}
                  </p>
                </div>
                {renderPayButton()}
              </div>
            </div>
            <p className="mt-2 px-2 text-center text-[10px] leading-tight text-slate-500 sm:text-[11px]">
              Secured by the gateway. Credits are added only after the payment is verified.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {completedPayment && (
        <PaymentSuccessModal
          payment={completedPayment}
          gateway={completedGateway}
          company={company}
          onClose={() => window.location.reload()}
        />
      )}

      {/* The payment step, one deliberate click after the plan is chosen. */}
      <PaymentCheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        planName={selected.name}
        planCredits={selected.credits}
        subtotal={quote ? quote.subtotal : selected.price}
        total={displayedTotal}
        taxLines={taxLines}
        gateways={gateways}
        selectedGateway={selectedGateway}
        onSelectGateway={setSelectedGateway}
        needsPhone={needsPhone}
        customerPhone={customerPhone}
        onCustomerPhoneChange={setCustomerPhone}
        processing={processing}
        onConfirm={handleSubmit}
      />
    </div>
  );
};

export default SubscriptionUI;